import { useState, useEffect, type ComponentType } from 'react'
import { useDialog } from 'lib/hooks/useDialog/useDialog'

export interface ModalWithHookedConditionalProps {
  id?: string
  /*
    onExited callback for conditionally rendered modals.
    Should not be passed directly.
  */
  _onConditionalExited?(): void
}

export const withHookedConditional = <T extends ModalWithHookedConditionalProps = ModalWithHookedConditionalProps>(
  WrappedComponent: ComponentType<T>,
  defaultId?: string,
) => {
  const ConditionalComponent: Settle.FC<Omit<T, '_onConditionalExited'>> = ({ children, ...props }) => {
    const id = props.id ?? defaultId

    if (!id) {
      throw new Error('Missing modal id')
    }

    const [open] = useDialog(id)
    const [isRendered, setIsRendered] = useState(open)

    useEffect(() => {
      if (open) {
        setIsRendered(true)
      }
    }, [open])

    if (isRendered) {
      return (
        <WrappedComponent
          // "as T" is workaround for https://github.com/Microsoft/TypeScript/issues/28938#issuecomment-450636046
          {...(props as T)}
          id={id}
          _onConditionalExited={() => setIsRendered(false)}
        >
          {children}
        </WrappedComponent>
      )
    }

    return null
  }

  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component'

  ConditionalComponent.displayName = `withHookedConditional(${displayName})`
  return ConditionalComponent
}
